import {useMemo, useEffect, useState} from 'react'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {useQuery, UseQueryResult} from '@tanstack/react-query'
import {PageIdType, CategoryType, ProductSearchType, PageDesignerType, ProductSearchHits} from '_types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {API} from '_customHooks'
import {resolveRedirect, resolveSearchRedirect} from '_utils'
import {useCookies} from 'react-cookie'

type CustomSdkData = {
    pageIdData: PageIdType
    categoryData: CategoryType
    productData: ProductSearchType
    pageDesignerData: PageDesignerType
    clientRedirect?: ReturnType<typeof resolveRedirect>
}

type ProductSearchHit = NonNullable<NonNullable<ProductSearchType>['hits']>[number]

const CACHEABLE_PRODUCT_EXPAND = 'images'
const PRICES_ONLY_EXPAND = 'prices'

async function fetchAndMergeProducts(api, params, headers) {
    const limit = params.limit
    let totalToFetch = params.offset

    // Prima richiesta per ottenere il totale disponibile
    const firstResponse = await api.shopperSearch.productSearch({
        parameters: {...params, offset: 0, limit},
        headers,
    })

    const totalAvailable = firstResponse?.total ?? 0
    // Limita totalToFetch al massimo al totale disponibile
    totalToFetch = Math.min(totalToFetch, totalAvailable)

    let mergedHits: ProductSearchHit[] = []
    const mergedResponse: ProductSearchType = {...firstResponse}

    const productsFetched = firstResponse?.hits?.length ?? 0

    if (firstResponse?.hits) mergedHits = [...firstResponse.hits]

    const remainingToFetch = totalToFetch - productsFetched
    if (remainingToFetch > 0) {
        const parallelRequests: Array<Promise<ProductSearchType>> = []
        let currentOffset = productsFetched

        while (currentOffset < totalToFetch) {
            const remaining = totalToFetch - currentOffset
            const currentLimit = Math.min(limit, remaining)
            parallelRequests.push(
                api.shopperSearch.productSearch({
                    parameters: {...params, offset: currentOffset, limit: currentLimit},
                    headers,
                }),
            )
            currentOffset += currentLimit
        }

        const parallelResponses = await Promise.all(parallelRequests)
        parallelResponses.forEach((response) => {
            if (response?.hits) mergedHits = [...mergedHits, ...response.hits]
        })
    }

    if (mergedResponse) mergedResponse.hits = mergedHits.slice(0, totalToFetch)

    return mergedResponse
}

const useCustomSdk = ({
    type,
    urlSegment,
    params,
    inheritedPageId,
    inheritedCategory,
    userLoadMore,
    searchMode = 'full',
    dyContext,
}): UseQueryResult<CustomSdkData> => {
    const api = useCommerceApi()
    const {locale} = useMultiSite()
    const {res} = useServerContext()
    const [allProducts, setAllProducts] = useState<ProductSearchHits>([])
    const [cookies] = useCookies()

    const {app, customParams} = getConfig()
    const siteId = app.commerceAPI.parameters.siteId
    const isProduction = customParams.isProduction
    const env = app.commerceAPI.parameters.organizationId

    const siteApi = useMemo(
        () =>
            new API({
                env,
                siteId,
                isProduction,
            }),
        [env, siteId, isProduction],
    )

    const aliasCountry = locale?.alias?.split('-')?.[1]?.toLowerCase()
    const {getTokenWhenReady} = useAccessToken()
    const queryKey = ['urlMapping', urlSegment, searchMode]

    let categoryData = inheritedCategory

    const sdkResponse = useQuery<CustomSdkData>(
        queryKey,
        async () => {
            const pathnameWithoutLocale = urlSegment.replace(/^\/[a-z]{2}-[a-z]{2}/, '')

            const token = await getTokenWhenReady()
            let pageIdData, pageDesignerData, productData
            // Client-only redirect decision (SPA navigation). Populated from any backend 301
            // source (url-mapping breadcrumb/PLP, product-search). Followed by the consuming page.
            let clientRedirect: ReturnType<typeof resolveRedirect> | undefined

            const isServer = typeof window === 'undefined'

            const headers = {
                authorization: `Bearer ${token}`,
            }

            if (!params.isSearch) {
                const hasMatchingInheritedPageId = Boolean(
                    inheritedPageId && (inheritedPageId.urlSegment === urlSegment || (params.cgid && inheritedPageId.resourceId === params.cgid)),
                )

                if ((!inheritedPageId || !hasMatchingInheritedPageId) && !params.cgid) {
                    const urlMapResponse = await api.shopperSeo.getUrlMapping({parameters: {urlSegment: pathnameWithoutLocale}, headers: headers})

                    if (urlMapResponse) {
                        pageIdData = urlMapResponse
                        pageIdData.urlSegment = urlSegment
                    }
                } else {
                    if (inheritedPageId) {
                        pageIdData = inheritedPageId
                    } else if (params.cgid) {
                        pageIdData = {
                            resourceId: params.cgid,
                        }
                    }
                }

                // getUrlMapping returns statusCode as a string; both 301 (permanent) and 302
                // (temporary) are redirects that must be followed.
                const isPageRedirect = pageIdData?.statusCode === '301' || pageIdData?.statusCode === '302'

                if (isPageRedirect) {
                    if (isServer) {
                        // res.redirect(statusCode, pageIdData.c_destinationUrl)
                        res.status(parseInt(pageIdData.statusCode, 10))
                        res.set('Location', pageIdData.c_destinationUrl)
                    } else {
                        // Client-side (SPA navigation, e.g. breadcrumb / internal PLP link):
                        // resolve the destination and let the page follow it via React Router
                        // (PLP/editorial in PWA) or a hard navigation (SFRA). Otherwise we would
                        // render an empty search/PLP page for a URL that the backend redirects.
                        clientRedirect = resolveRedirect({url: pageIdData.c_destinationUrl, status: pageIdData.statusCode})
                    }
                }

                // A non-canonical product URL (e.g. a bare variant id `.html`) matches the PLP
                // route (single path segment) but getUrlMapping resolves it to a PRODUCT resource
                // WITHOUT a redirect statusCode (so isPageRedirect is false). Without this branch
                // the hook would treat the product resourceId as a category id, getCategory would
                // fail, and the PLP would render an empty HTTP 200. Instead, redirect (301) to the
                // product's canonical PDP URL, reusing the same plumbing as the page-301 case.
                const isProductMapping = pageIdData?.resourceType?.toLowerCase() === 'product'

                if (!isPageRedirect && isProductMapping) {
                    // Prefer a destination URL the mapping already carries. `c_destinationUrl` is the
                    // field the page-301 branch above reads; `destinationUrl` is the standard SCAPI
                    // field. Fall back to resolving the product and reading its canonical URL.
                    let productCanonicalUrl: string | undefined = pageIdData?.c_destinationUrl || (pageIdData as {destinationUrl?: string})?.destinationUrl

                    if (!productCanonicalUrl && pageIdData?.resourceId) {
                        try {
                            const productResponse = await api.shopperProducts.getProduct({
                                parameters: {
                                    id: pageIdData.resourceId,
                                    locale: locale?.id,
                                    c_pdp: true,
                                },
                                headers: headers,
                            })
                            productCanonicalUrl = productResponse?.c_seo?.canonicalUrl
                        } catch (error) {
                            console.error('Error resolving product canonical url:', error)
                        }
                    }

                    if (productCanonicalUrl) {
                        if (isServer) {
                            res.status(301)
                            res.set('Location', productCanonicalUrl)
                        } else {
                            clientRedirect = resolveRedirect({url: productCanonicalUrl, status: 301})
                        }
                    }

                    // Return early with the product-mapping result so the hook does NOT fall through
                    // to the category / product-search branches. If no canonical URL was resolvable
                    // we intentionally do not produce a redirect here (and avoid an empty 200 by not
                    // treating the product id as a category).
                    return {pageIdData, categoryData: null, productData: undefined, pageDesignerData: undefined, clientRedirect}
                }

                if (!isPageRedirect) {
                    if (type === 'category' || type === 'product' || type === 'pageDesigner') {
                        const resolvedCategoryId = pageIdData?.resourceId || params.cgid || inheritedCategory?.id
                        const canReuseCategory = Boolean(inheritedCategory?.id && resolvedCategoryId && inheritedCategory.id === resolvedCategoryId)

                        if (canReuseCategory) {
                            categoryData = inheritedCategory
                        } else {
                            const categoryParams: {id: string; c_template?: string} = {
                                id: pageIdData?.resourceId,
                            }
                            if (params?.c_template) {
                                categoryParams.c_template = params?.c_template
                            }

                            try {
                                const categoryResponse = await api.shopperProducts.getCategory({
                                    parameters: categoryParams,
                                    headers: headers,
                                })
                                if (params.cgid && categoryResponse?.c_canonicalUrl) {
                                    res.status(301)
                                    res.set('Location', categoryResponse?.c_canonicalUrl)
                                }
                                categoryData = categoryResponse
                            } catch (error) {
                                console.error('Error fetching category data:', error)
                                categoryData = null
                            }
                        }
                    }
                }
            }

            const isSimpleSearch = Boolean(params?.isSearch && params?.q)
            const standardPageMatchesUrl = !pageIdData?.urlSegment || pageIdData.urlSegment === urlSegment
            const standardCategoryMatchesPage = !pageIdData?.resourceId || pageIdData.resourceId === categoryData?.id
            const categoryActivation = categoryData?.c_dynamicYieldActivation
            const shouldUseDyInStandard =
                Boolean(categoryActivation) &&
                Boolean(dyContext?.hasDynamicYieldConfig) &&
                Boolean(categoryData?.id) &&
                !isSimpleSearch &&
                standardPageMatchesUrl &&
                standardCategoryMatchesPage &&
                (categoryActivation === 'DY' || (categoryActivation === 'AB' && dyContext?.abDyBucket && dyContext?.dyConsentAccepted))

            if (
                !shouldUseDyInStandard &&
                searchMode === 'full' &&
                ((pageIdData?.statusCode !== '301' && type === 'product' && categoryData) || params.isSearch)
            ) {
                let hasHiddenPrices = false
                let pageDesignerPromise, productSearchPromise
                if (params.isSearch) {
                    const customPrefRes = await siteApi.getCustomPreference('CountriesWithZeroPrices', token)
                    hasHiddenPrices = customPrefRes?.['CountriesWithZeroPrices']?.find((country) => country.toLowerCase() === aliasCountry)
                } else {
                    hasHiddenPrices = categoryData.c_countriesWithZeroPrices
                    pageDesignerPromise = api.shopperExperience.getPages({
                        parameters: {
                            categoryId: categoryData.id,
                            locale: locale?.id,
                            aspectTypeId: 'plp',
                            aspectAttributes: `{"category": "${categoryData.id}"}`,
                        },
                        headers: headers,
                    })
                }
                if (hasHiddenPrices && params) {
                    if (params.refine) {
                        params.refine = params.refine.filter((filter) => !filter.startsWith('price='))
                    }
                }

                const limit = params.c_template === 'search/grid3Template' ? 21 : 20
                const hasExplicitTemplate = params.c_template === 'search/grid2Template' || params.c_template === 'search/grid3Template'
                // On /search there is no categoryData and no c_template in the URL until the grid toggle is used.
                // Search defaults to the grid3 layout (21 per page), so align the API limit with the grid to avoid an empty tile.
                const searchDefaultLimit = params.isSearch ? 21 : limit

                params.limit = hasExplicitTemplate ? limit : categoryData?.c_pageSize ? categoryData?.c_pageSize : searchDefaultLimit

                if (params.sz <= params.limit) {
                    params.offset = 0
                    setAllProducts([])
                }

                const isVipUser = 'srgswr_vc' in cookies && cookies['srgswr_vc'] === 'true'
                const isSaleCategory = params.refine?.some((filter) => filter.startsWith('pmid=sale'))
                params.expand = isVipUser || isSaleCategory ? PRICES_ONLY_EXPAND : CACHEABLE_PRODUCT_EXPAND

                if (!params.isSearch && pageIdData?.resourceId) {
                    params?.refine?.push(`cgid=${pageIdData?.resourceId}`)
                } else if (!params.isSearch && userLoadMore.current) {
                    params?.refine?.push(`cgid=${categoryData?.id}`)
                }
                // Check if params.refine includes a string that starts with 'pmid='
                const hasPromotionFilterExplicitlyAdded = params.refine?.some((filter) => filter.startsWith('pmid=')) || params.isSearch
                if (!hasPromotionFilterExplicitlyAdded) {
                    const activePromo = categoryData?.c_promoNosale || categoryData?.c_promoNopreview
                    if (activePromo) {
                        params.refine.push(`pmid=${activePromo}`)
                    }
                } else {
                    if (params.isSearch && categoryData?.c_promoNopreview) {
                        params.refine.push(`pmid=${categoryData?.c_promoNopreview}`)
                    }
                }

                try {
                    if (params.offset > 0 && !userLoadMore.current) {
                        productSearchPromise = fetchAndMergeProducts(api, params, headers)
                    } else {
                        if (params.offset > params.limit) {
                            params.offset = params.offset - params.limit
                        }
                        productSearchPromise = api.shopperSearch.productSearch({
                            parameters: {...params},
                            headers: headers,
                        })
                        if (userLoadMore?.current) {
                            userLoadMore.current = false
                        }
                    }

                    let pageDesignerResponse, productResponse
                    if (pageDesignerPromise) {
                        ;[pageDesignerResponse, productResponse] = await Promise.all([pageDesignerPromise, productSearchPromise])
                        pageDesignerData = pageDesignerResponse?.data[0]?.regions
                    } else {
                        productResponse = await Promise.resolve(productSearchPromise)
                    }

                    if (allProducts && allProducts?.length > 0 && productResponse?.hits && params.sz > params.limit) {
                        const prevIds = new Set(allProducts?.map((p) => p?.productId))
                        const filteredHits = productResponse.hits.filter((p) => !prevIds.has(p.productId))
                        productResponse.hits = [...allProducts, ...filteredHits]
                    }

                    productData = productResponse
                } catch (error) {
                    if (pageDesignerPromise) {
                        pageDesignerData = null
                        console.error('Error fetching product data or page designer data:', error)
                    } else {
                        console.error('Error fetching product data:', error)
                    }
                    productData = null
                    throw error
                }

                const searchRedirectStatus = productData?.searchPhraseSuggestions?.c_searchRedirectStatus
                const isSearchRedirect = searchRedirectStatus === 301 || searchRedirectStatus === 302
                if (isSearchRedirect) {
                    if (isServer) {
                        // SSR / direct landing: emit a real redirect. On direct landing the ECDN
                        // routing rules already handle this, but we keep it for parity.
                        // res.redirect(status, productData?.searchPhraseSuggestions?.c_searchRedirectUrl)
                        res.status(searchRedirectStatus)
                        res.set('Location', productData?.searchPhraseSuggestions?.c_searchRedirectUrl)
                    } else {
                        // Client-side (SPA navigation from the header search): there is no HTTP
                        // response to redirect. Resolve the target and expose it so the page can
                        // follow it via React Router (editorial /stories pages) or a hard
                        // navigation (SFRA pages).
                        clientRedirect = resolveSearchRedirect(productData)
                    }
                }
            }

            return {pageIdData, categoryData, productData, pageDesignerData, clientRedirect}
        },
        {
            staleTime: 1000 * 10,
            cacheTime: 1000 * 60 * 3,
            keepPreviousData: true,
        },
    )

    useEffect(() => {
        if (sdkResponse.data?.productData?.hits) {
            const newHits = sdkResponse.data.productData.hits
            setAllProducts((prev) => {
                if (params.offset > 0) {
                    const safePrev = prev ?? []
                    const prevIds = new Set(safePrev.map((p) => p?.productId))
                    const filteredHits = newHits.filter((p) => !prevIds.has(p.productId))
                    return [...(Array.isArray(prev) ? prev : []), ...(Array.isArray(filteredHits) ? filteredHits : [])]
                } else {
                    return newHits
                }
            })
        }
    }, [sdkResponse.data?.productData?.hits, params.offset])

    return sdkResponse
}

export {useCustomSdk}
